// FireSafetyParametersTable.tsx
import React from 'react';

interface FireSafetyParameter {
  parameter: string;
  inPercent: number | null;
  score: number | null;
  maxScore: number;
  percentage: number | null;
}

interface FireSafetyParametersTableProps {
  data?: FireSafetyParameter[];
  onChange?: (index: number, updatedRow: FireSafetyParameter) => void;
  isEditable?: boolean;
}

export default function FireSafetyParametersTable({
  data = [],
  onChange,
  isEditable = true
}: FireSafetyParametersTableProps) {
  const calculateDerivedFields = (score: number | null, maxScore: number) => {
    if (score === null || score === undefined || maxScore <= 0) {
      return { inPercent: 0, percentage: 0 };
    }
    const inPercent = maxScore > 0 ? score / maxScore : 0;
    const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
    return { inPercent, percentage };
  };

  const handleScoreChange = (idx: number, newScore: string) => {
    if (!onChange || !isEditable) return;

    // Handle N/A option
    if (newScore.toLowerCase() === 'na' || newScore.toLowerCase() === 'n/a' || newScore === '') {
      const row = data[idx];
      onChange(idx, {
        ...row,
        score: null as any,
        inPercent: 0,
        percentage: 0
      });
      return;
    }

    const numScore = parseFloat(newScore);
    if (isNaN(numScore)) return;

    const row = data[idx];
    const { inPercent, percentage } = calculateDerivedFields(numScore, row.maxScore);

    onChange(idx, {
      ...row,
      score: numScore,
      inPercent,
      percentage: Math.round(percentage * 100) / 100
    });
  };

  const formatValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "N/A";
    return value.toFixed(2);
  };

  const getScoreDisplayValue = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return "";
    return score.toString();
  };

  // Calculate totals
  const totalScore = data.reduce((sum, row) => {
    return sum + (row.score !== null && row.score !== undefined ? row.score : 0);
  }, 0);

  const totalMaxScore = data.reduce((sum, row) => {
    return sum + (row.maxScore !== null && row.maxScore !== undefined ? row.maxScore : 0);
  }, 0);

  // Calculate total aggregate percentage: (Total Score / Total Max Score) * 100
  const totalAggregatePercentage = totalMaxScore > 0
    ? (totalScore / totalMaxScore) * 100
    : 0;

  return (
    <div className="w-full overflow-x-auto">
      <table className="min-w-full border-collapse border border-gray-300 bg-yellow-50">
        <thead>
          <tr className="bg-yellow-200 border-b-2 border-gray-400">
            <th className="border border-gray-300 px-4 py-3 text-left font-bold text-sm text-gray-800">
              PARAMETERS OF FIRE SAFETY
            </th>
            <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm text-gray-800">
              In %
            </th>
            <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm text-gray-800">
              SCORE
            </th>
            <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm text-gray-800">
              MAX SCORE
            </th>
            <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm text-gray-800">
              PERCENTAGE (%)
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx} className="bg-yellow-50 hover:bg-yellow-100 transition-colors">
              <td className="border border-gray-300 px-4 py-3 text-sm text-gray-700">
                {row.parameter}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-700 font-medium">
                {formatValue(row.inPercent)}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center">
                {/* Score is always read-only - auto-calculated from MIS Checklist
                    - N/A (null) in MIS → Full score (maxScore)
                    - 0 in MIS → 0 score
                    - Value > 0 in MIS → Calculated proportionally */}
                <span className="text-sm text-gray-700 font-medium">
                  {row.score !== null && row.score !== undefined ? row.score.toFixed(2) : "N/A"}
                </span>
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-700 font-medium">
                {row.maxScore !== null && row.maxScore !== undefined ? row.maxScore.toFixed(2) : "N/A"}
              </td>
              <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-700 font-medium">
                {formatValue(row.percentage)}
              </td>
            </tr>
          ))}
          {/* Total Row */}
          <tr className="bg-yellow-300 border-t-2 border-gray-500 font-bold">
            <td className="border border-gray-300 px-4 py-3 text-sm text-gray-800">
              TOTAL
            </td>
            <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-800">
              {formatValue(totalAggregatePercentage / 100)}
            </td>
            <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-800">
              {totalScore.toFixed(2)}
            </td>
            <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-800">
              {totalMaxScore.toFixed(2)}
            </td>
            <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-800">
              {totalAggregatePercentage.toFixed(2)}%
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
